import React, { useEffect, useState, useCallback } from 'react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Receipt, Plus, Eye, FileText, FilePlus, Search, ArrowLeft, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import InvoiceActions from '@/components/invoices/components/InvoiceActions';
import InvoiceStatusBadge from '@/components/invoices/components/InvoiceStatusBadge';
import InvoiceCard from '@/components/invoices/InvoiceCard';
import { FilterBuilder } from '@/components/invoices/FilterBuilder';
import { useInvoiceData } from './hooks/useInvoiceData';
import { useFilterBuilder } from './hooks/useFilterBuilder';
import { useViewPreference } from '@/hooks/useViewPreference';
import { getCustomerDisplayName } from '@/utils/customerDisplayHelpers';
import { formatCurrency } from '@/lib/utils';
import { getInvoiceTypeBadge } from '@/components/invoices/utils/invoiceTypeBadge';
import PageLayout from '@/components/layout/PageLayout';
import PageHeader from '@/components/layout/PageHeader';
import DataTableWrapper from '@/components/shared/DataTableWrapper';
import ViewToggle from '@/components/shared/ViewToggle';
import FilterToolbar from '@/components/shared/FilterToolbar';

type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'partial';

const InvoicesPage = () => {
  const {
    invoices,
    fetchInvoices,
    search,
    setSearch,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir,
  } = useInvoiceData();
  
  const {
    filteredInvoices,
    draftGroups,
    appliedGroups,
    setDraftGroups,
    setAppliedGroups,
    filterFields,
    filterEntities,
    filterAttributesByEntity,
    addGroup,
    updateGroup,
    removeGroup,
    addRuleToGroup,
    updateRuleInGroup,
    removeRuleFromGroup,
    clearAllRules,
    applyFilters,
    hasActiveFilters,
    hasUnappliedChanges,
    canApplyFilters,
  } = useFilterBuilder(invoices);

  
  const { viewMode, setViewMode } = useViewPreference({ entityType: 'invoices', defaultView: 'list' });
  
  const [isLoading, setIsLoading] = React.useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [dialogView, setDialogView] = useState<'choice' | 'estimate-picker'>('choice');
  const [estimateSearch, setEstimateSearch] = useState('');
  const [estimates, setEstimates] = useState<any[]>([]);
  const [estimatesLoading, setEstimatesLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Seed filter from ?status=overdue (e.g. dashboard "View all overdue invoices" link)
  useEffect(() => {
    const statusParam = searchParams.get('status');
    if (statusParam === 'overdue' && appliedGroups.length === 0) {
      const group = {
        id: `group_${Date.now()}_${Math.random()}`,
        rules: [{
          id: `rule_${Date.now()}_${Math.random()}`,
          entity: 'invoice',
          attribute: 'status',
          operator: 'equals' as const,
          value: 'overdue',
          logic: undefined,
        }],
        logic: 'AND' as const,
      };
      setDraftGroups([group]);
      setAppliedGroups([group]);
      const next = new URLSearchParams(searchParams);
      next.delete('status');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const fetchApprovedEstimates = useCallback(async () => {
    setEstimatesLoading(true);
    try {
      const { data, error } = await supabase
        .from('estimates')
        .select(`
          id,
          estimate_number,
          title,
          status,
          created_at,
          customers ( name, last_name )
        `)
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching estimates:', error);
        return;
      }

      // Calculate totals
      const withTotals = await Promise.all(
        (data || []).map(async (est) => {
          const { data: items } = await supabase
            .from('estimate_line_items')
            .select('total_cost_snapshot, manual_override, manual_total_cost')
            .eq('estimate_id', est.id);
          const total = items?.reduce((sum, i) => {
            const cost = i.manual_override && i.manual_total_cost ? i.manual_total_cost : i.total_cost_snapshot;
            return sum + (cost || 0);
          }, 0) || 0;
          return { ...est, total_amount: total };
        })
      );
      setEstimates(withTotals);
    } finally {
      setEstimatesLoading(false);
    }
  }, []);

  useEffect(() => {
    const loadInvoices = async () => {
      setIsLoading(true);
      await fetchInvoices();
      setIsLoading(false);
    };
    loadInvoices();
  }, []);

  return (
    <PageLayout>
      <PageHeader 
        title="Invoices"
        description="Manage your invoices and billing"
      />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              All Invoices
            </CardTitle>
            <ViewToggle value={viewMode} onChange={setViewMode} />
          </div>
          
          {/* Unified FilterToolbar */}
          <FilterToolbar
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search invoices..."
            sortOptions={[
              { value: 'created_at', label: 'Date Created' },
              { value: 'invoice_number', label: 'Invoice Number' },
              { value: 'total_amount', label: 'Total Amount' },
              { value: 'due_date', label: 'Due Date' },
            ]}
            sortValue={sortBy}
            onSortChange={setSortBy}
            sortDir={sortDir}
            onSortDirChange={setSortDir}
            actions={
              <Button size="sm" onClick={() => setShowCreateDialog(true)} className="gap-1.5">
                <Plus className="h-4 w-4 shrink-0" />
                <span className="hidden sm:inline truncate">Create Invoice</span>
                <span className="sm:hidden truncate">New</span>
              </Button>
            }
            advancedFilter={
              <FilterBuilder
                draftGroups={draftGroups}
                appliedGroups={appliedGroups}
                fields={filterFields}
                entities={filterEntities}
                attributesByEntity={filterAttributesByEntity}
                onAddGroup={addGroup}
                onUpdateGroup={updateGroup}
                onRemoveGroup={removeGroup}
                onAddRuleToGroup={addRuleToGroup}
                onUpdateRuleInGroup={updateRuleInGroup}
                onRemoveRuleFromGroup={removeRuleFromGroup}
                onClearAll={clearAllRules}
                onApplyFilters={applyFilters}
                hasActiveFilters={hasActiveFilters}
                hasUnappliedChanges={hasUnappliedChanges}
                canApplyFilters={canApplyFilters}
              />
            }
            hasAdvancedFilters={hasActiveFilters}
            advancedFilterCount={appliedGroups.reduce((c, g) => c + g.rules.filter(r => r.entity && r.attribute && r.value).length, 0)}
            onClearAdvancedFilters={clearAllRules}
          />
        </CardHeader>
        <CardContent className={viewMode === 'list' ? 'p-0' : undefined}>
          <DataTableWrapper
            isLoading={isLoading}
            isEmpty={!filteredInvoices || filteredInvoices.length === 0}
            emptyState={{
              title: "No invoices yet",
              description: "Create your first invoice to get started.",
              icon: <Receipt className="h-12 w-12 text-muted-foreground" />,
              action: (
                <Button className="mt-4" onClick={() => setShowCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Invoice
                </Button>
              )
            }}
            skeletonColumns={9}
          >
            {viewMode === 'list' ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="hidden sm:table-cell w-28">Invoice #</TableHead>
                    <TableHead className="max-w-0 w-full">Title</TableHead>
                    <TableHead className="hidden md:table-cell">Customer</TableHead>
                    <TableHead className="hidden lg:table-cell">Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Amount</TableHead>
                    <TableHead className="hidden md:table-cell">Created</TableHead>
                    <TableHead className="hidden lg:table-cell">Due Date</TableHead>
                    <TableHead className="text-right w-16">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices?.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="hidden sm:table-cell font-mono text-sm whitespace-nowrap w-28">
                        {invoice.invoice_number || 'INV-PENDING'}
                      </TableCell>
                      <TableCell className="max-w-0 w-full">
                        <div className="min-w-0">
                          <Link
                            to={`/invoices/${invoice.id}`}
                            className="font-medium hover:underline block truncate"
                          >
                            {(invoice.title?.replace(/^Invoice for\s*/i, '') || 'Untitled') + ' INV'}
                          </Link>
                          <span className="sm:hidden text-xs text-muted-foreground font-mono">
                            {invoice.invoice_number || 'INV-PENDING'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {getCustomerDisplayName(invoice.customers)}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {getInvoiceTypeBadge(invoice.invoice_type || 'full', invoice.percentage_of_estimate)}
                      </TableCell>
                      <TableCell>
                        <InvoiceStatusBadge 
                          status={invoice.status as InvoiceStatus} 
                          dueDate={invoice.due_date}
                          size="sm"
                        />
                      </TableCell>
                      <TableCell className="text-right font-medium whitespace-nowrap font-mono">
                        {formatCurrency(invoice.total_amount || 0)}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {format(new Date(invoice.created_at), 'dd MMMM yyyy')}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {invoice.due_date ? (
                          <span className={
                            new Date(invoice.due_date) < new Date() && invoice.status !== 'paid'
                              ? 'text-destructive font-medium' 
                              : ''
                          }>
                            {format(new Date(invoice.due_date), 'dd MMMM yyyy')}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">No due date</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/invoices/${invoice.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          <InvoiceActions 
                            invoiceId={invoice.id}
                            currentStatus={invoice.status}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <InvoiceCard invoices={filteredInvoices || []} />
            )}
          </DataTableWrapper>
        </CardContent>
      </Card>

      {/* Create Invoice Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        setShowCreateDialog(open);
        if (!open) {
          setDialogView('choice');
          setEstimateSearch('');
        }
      }}>
        <DialogContent className={dialogView === 'estimate-picker' ? 'sm:max-w-lg' : 'sm:max-w-md'}>
          {dialogView === 'choice' ? (
            <>
              <DialogHeader>
                <DialogTitle>Create Invoice</DialogTitle>
                <DialogDescription>
                  How would you like to create your invoice?
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-3 py-2">
                <button
                  className="flex items-start gap-4 p-4 rounded-lg border border-border hover:bg-accent/50 transition-colors text-left"
                  onClick={() => {
                    setDialogView('estimate-picker');
                    fetchApprovedEstimates();
                  }}
                >
                  <FileText className="h-8 w-8 text-primary mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">From an Estimate</p>
                    <p className="text-sm text-muted-foreground">
                      Create an invoice linked to an approved estimate with auto-calculated line items and progress tracking.
                    </p>
                  </div>
                </button>
                <button
                  className="flex items-start gap-4 p-4 rounded-lg border border-border hover:bg-accent/50 transition-colors text-left"
                  onClick={() => {
                    setShowCreateDialog(false);
                    navigate('/invoices/create?mode=standalone');
                  }}
                >
                  <FilePlus className="h-8 w-8 text-primary mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">Standalone Invoice</p>
                    <p className="text-sm text-muted-foreground">
                      Create a new invoice with custom line items, without linking to an estimate.
                    </p>
                  </div>
                </button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDialogView('choice')}>
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div>
                    <DialogTitle>Select Estimate</DialogTitle>
                    <DialogDescription>Choose an approved estimate to create an invoice from.</DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by number, title, or customer..."
                  value={estimateSearch}
                  onChange={(e) => setEstimateSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <ScrollArea className="max-h-[400px]">
                {estimatesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (() => {
                  const q = estimateSearch.toLowerCase();
                  const filtered = estimates.filter((est) => {
                    const customerName = est.customers
                      ? `${est.customers.name || ''} ${est.customers.last_name || ''}`.toLowerCase()
                      : '';
                    return (
                      (est.estimate_number || '').toLowerCase().includes(q) ||
                      (est.title || '').toLowerCase().includes(q) ||
                      customerName.includes(q)
                    );
                  });
                  if (filtered.length === 0) {
                    return (
                      <div className="text-center py-8 text-muted-foreground">
                        <FileText className="h-10 w-10 mx-auto mb-2 opacity-20" />
                        <p className="text-sm">No approved estimates found</p>
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-2">
                      {filtered.map((est) => (
                        <button
                          key={est.id}
                          className="w-full flex items-center justify-between p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors text-left"
                          onClick={() => {
                            setShowCreateDialog(false);
                            navigate(`/invoices/create?estimate_id=${est.id}`);
                          }}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm font-medium">{est.estimate_number}</span>
                            </div>
                            <p className="text-sm truncate">{est.title}</p>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                              {est.customers && (
                                <span>{est.customers.name} {est.customers.last_name}</span>
                              )}
                              <span>{format(new Date(est.created_at), 'dd MMMM yyyy')}</span>
                            </div>
                          </div>
                          <span className="font-mono text-sm font-medium whitespace-nowrap ml-3">
                            {formatCurrency(est.total_amount)}
                          </span>
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </ScrollArea>
            </>
          )}
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default InvoicesPage;
